/**
 * Clients Domain Module
 *
 * Handles all client-related operations including:
 * - Client CRUD operations
 * - Bonus and loyalty management
 * - Client operations history
 * - Client queries by date range
 *
 * This module provides proper TypeScript types for client operations.
 */

import { format } from 'date-fns'
import { getDefaultStore } from 'jotai'
import * as Sentry from '@sentry/react-native'

// Firebase primitives (from firebase.ts to avoid circular deps)
import { rootDb, fieldValue, auth } from './firebase'

// Shared types and utilities
import {
  filterUndefined,
  shouldUseSupabase,
  snapshotExists,
  transformApiItem,
  type DateRange,
  type TimestampLike,
  type MockRef,
} from './types'

import { dbAtom, organizationAtom } from '../AtomsProvider'

import { clientsApi, clientOperationsApi, type ClientResponse } from '../API/typedApiClient'
import { readWholeList, newestFirst } from '../API/readWholeList'
import { bestEffortDualWrite, bestEffortDualWriteDelete } from './dualWrite'
import { isEntityAbsentFailure } from './dualWriteOutbox'
import { normalizeClearedLoyaltyFields, normalizeClientPhoneFields } from './clientFieldNormalization'
import { normalizePhoneText } from '../../utils/phoneNumber'
import { assertWWriterAvailable } from '../Write/writeW'
import { commitGuardedCreate } from './writeClaims'

// ============================================================================
// Types
// ============================================================================

/**
 * The client's auto-applied loyalty discount (ATL-688).
 *
 * Stored and round-tripped VERBATIM: exactly one of `percentage`/`amount` carries the value,
 * while `title`/`text` are the FormSelect labels ClientDetailScreen writes back — they are part
 * of the value, not display chrome. NewSaleScreen reads `.percentage`/`.amount` to pre-apply the
 * discount to the cart, so this field changes what the customer is charged.
 */
export interface LoyaltyDiscount {
  percentage?: number
  amount?: number
  title?: string
  text?: string
}

/**
 * Client information
 */
export interface Client {
  id?: string
  ref?: MockRef
  organizationId: string

  // Basic info
  name?: string
  phone?: string
  phoneNumber?: string
  email?: string
  birthday?: Date | TimestampLike | string
  notes?: string
  address?: string

  // Bonus and loyalty
  bonusValue?: number
  bonusBalance?: number
  bonus?: number
  punchCardValue?: number
  punchCardPunches?: number
  /** Bonus/cashback accrual percent. Undefined/null = unset; CartView falls back to 3. */
  bonusLevel?: number | null
  loyaltyDiscount?: LoyaltyDiscount | null
  /** price_types firestore id this client is charged at by default. */
  preferredPriceType?: string | null

  // Purchase history
  totalPurchasedValue?: number
  totalPurchases?: number
  totalVisits?: number
  lastPurchaseDate?: Date | TimestampLike
  lastPurchaseSource?: string
  lastVisitDate?: Date | TimestampLike

  // Registration
  registrationDate?: Date | TimestampLike
  registrationSource?: string
  dailyStatID?: string

  // Audit
  createdAt?: Date | TimestampLike
  updatedAt?: Date | TimestampLike
}

/**
 * Client grouped by date for display
 */
export interface ClientsByDate {
  title: string
  data: Client[]
}

/**
 * Client operation (bonus change, etc.)
 */
export interface ClientOperation {
  id?: string
  ref?: MockRef
  clientId: string
  operationType: 'bonusChange' | 'bonus_add' | 'bonus_use' | 'purchase' | 'refund'
  operationValue?: number
  /** 'delta' when the Supabase mirror is the keyed bonus adjustment (ATL-1445 stage 2c). */
  mirror?: 'delta'
  amount?: number
  bonusBefore?: number
  bonusAfter?: number
  date: Date | TimestampLike
  userId?: string
  linkedDocId?: string
  linkedDocType?: string
  createdByFirestoreId?: string
}

/**
 * Parameters for creating a client
 */
export interface CreateClientParams {
  organizationId: string
  name?: string
  displayName?: string
  phone?: string
  phoneNumber?: string
  email?: string
  birthday?: Date | TimestampLike | string
  bonusValue?: number
  bonusLevel?: number
  punchCardValue?: number
  notes?: string
  address?: string
  clientType?: string
  loyaltyDiscount?: LoyaltyDiscount | null
  preferredPriceType?: string | null
  registrationDate: Date
  registrationSource: string
  dailyStatID: string
}

/**
 * Parameters for updating a client
 */
export interface UpdateClientParams {
  name?: string
  displayName?: string
  phoneNumber?: string
  email?: string
  address?: string
  birthday?: Date | TimestampLike | string
  bonus?: number
  punchCardPunches?: number
  notes?: string
  // ATL-688: the ClientDetailScreen edit modal has always written these three to Firestore,
  // but they were absent from this type and from the dual-write payload — so every edit of a
  // client's discount / bonus rate / price tier silently diverged the two stores.
  bonusLevel?: number | string | null
  loyaltyDiscount?: LoyaltyDiscount | null
  preferredPriceType?: string | null
}

/**
 * API response for client (snake_case from backend)
 */
// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get Jotai store and database reference
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Prepared for future refactoring
const _getDbContext = () => {
  const jotaiStore = getDefaultStore()
  const db = jotaiStore.get(dbAtom)
  const organization = jotaiStore.get(organizationAtom)
  return {
    jotaiStore,
    db,
    organization,
    organizationId: (organization as { id?: string })?.id,
  }
}

/**
 * Normalize API client response to Firestore-like shape
 * (no snake_case mapping; only legacy alias fallbacks)
 */
const normalizeClient = (apiClient: ClientResponse): Client => {
  const transformed = transformApiItem(apiClient as Record<string, unknown>, 'clients') as unknown as Client

  if (transformed.bonusValue === undefined && transformed.bonusBalance !== undefined) {
    transformed.bonusValue = transformed.bonusBalance
  }

  if (transformed.totalPurchasedValue === undefined && transformed.totalPurchases !== undefined) {
    transformed.totalPurchasedValue = transformed.totalPurchases
  }

  return transformed
}

/**
 * Convert date to ISO string for API calls
 */
const toApiDate = (date: Date | TimestampLike | string | undefined): string | null => {
  if (!date) return null
  if (typeof date === 'string') return date
  if (date instanceof Date) return date.toISOString()
  if (typeof (date as TimestampLike).toDate === 'function') {
    return (date as TimestampLike).toDate().toISOString()
  }
  return null
}

/**
 * Group clients by date for display
 */
const groupClientsByDate = (
  clients: Client[],
  dateField: 'registrationDate' | 'lastPurchaseDate'
): ClientsByDate[] => {
  const grouped: Record<string, Client[]> = {}

  clients.forEach((client) => {
    const dateValue = client[dateField]
    const date = dateValue && typeof (dateValue as TimestampLike).toDate === 'function'
      ? (dateValue as TimestampLike).toDate()
      : new Date(dateValue as string | Date)
    const dateKey = format(date, 'd/M/y EEEE')

    if (!grouped[dateKey]) {
      grouped[dateKey] = []
    }
    grouped[dateKey].push(client)
  })

  return Object.entries(grouped).map(([title, data]) => ({ title, data }))
}

// ============================================================================
// Query Functions
// ============================================================================

/**
 * Find a client by phone number
 */
export const findClientByPhone = async (
  db: unknown,
  phone: string
): Promise<Client | null> => {
  // ATL-1523: a phone can arrive with or without the invisible characters a pasted number carries (a
  // sale's snapshot keeps what was stored then; a stored phone is cleaned on its next save or by the
  // backfill). The two forms are the same number, so both match — but an EXACT match wins: two
  // different clients can differ only by those characters (17 such pairs in prod, 2026-09-27), and
  // the exact form is what told them apart before. Resolving a sale's client by PHONE at all is the
  // underlying defect (ATL-1513: resolve by id); until then this keeps today's answer where it had one.
  const cleaned = normalizePhoneText(phone) as string
  if (shouldUseSupabase()) {
    try {
      const clients = await clientsApi.searchClients(cleaned)

      if (clients.length > 0) {
        const exactMatch =
          clients.find((client) => client.phone === phone || client.phoneNumber === phone) ||
          clients.find(
            (client) =>
              normalizePhoneText(client.phone) === cleaned || normalizePhoneText(client.phoneNumber) === cleaned
          )
        const client = normalizeClient((exactMatch || clients[0]) as ClientResponse)
        if (clients.length > 1) {
          console.warn('Multiple clients found with phone:', phone, clients)
        }
        return client
      } else {
        return null
      }
    } catch (error) {
      console.error('Error finding client by phone from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'clients', function: 'findClientByPhone' },
        extra: { phone, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation
  const querySnapshot = await (db as {
    collection: (name: string) => {
      where: (field: string, op: string, value: unknown) => {
        get: () => Promise<{
          size: number
          docs: Array<{ id: string; ref: MockRef; data: () => Client }>
        }>
      }
    }
  }).collection('clients').where('phoneNumber', 'in', cleaned === phone ? [phone] : [phone, cleaned]).get()

  if (querySnapshot.size > 0) {
    const doc = querySnapshot.docs.find((d) => d.data().phoneNumber === phone) ?? querySnapshot.docs[0]
    const client = doc.data()
    client.id = doc.id
    client.ref = doc.ref
    return client
  }
  return null
}

/**
 * Fetch a single full client record by Firestore id from the active read source.
 *
 * Client SEARCH is Supabase-backed (ATL-680), but search hits are lightweight and
 * omit fields the sale flow needs — notably the nested `loyaltyDiscount` object.
 * Reads still come from Firestore until an org is on Supabase read mode, so the
 * full client must be re-fetched from the current read source after a search hit
 * is selected. Returns null if the client no longer exists.
 */
export const getClientById = async (
  db: unknown,
  firestoreId: string
): Promise<Client | null> => {
  if (shouldUseSupabase()) {
    try {
      return normalizeClient(await clientsApi.getClientById(firestoreId))
    } catch (error) {
      // The backend answers an absent client with 404: the same "no such client" the Firestore path
      // returns as null. Only a GENUINE entity absence — not a dead backend or a missing route (the
      // ATL-1188 classifier) — so an outage never reads as "the client was deleted".
      if (isEntityAbsentFailure(error)) return null
      throw error
    }
  }

  // Firestore implementation (default read source)
  const snapshot = await (db as {
    collection: (name: string) => {
      doc: (id: string) => {
        get: () => Promise<{ exists: unknown; id: string; ref: MockRef; data: () => Client }>
      }
    }
  }).collection('clients').doc(firestoreId).get()

  // `exists` is a boolean property on web (firebase/compat) but a method on native
  // (@react-native-firebase/firestore v22) — use the platform-safe guard.
  if (!snapshotExists(snapshot)) return null

  const client = snapshot.data()
  client.id = snapshot.id
  client.ref = snapshot.ref
  return client
}

/**
 * Get clients registered within a date range
 */
export const getClientsRegisteredOnDateRange = async (
  db: unknown,
  range: DateRange,
  flat = false
): Promise<Client[] | ClientsByDate[]> => {
  if (shouldUseSupabase()) {
    try {
      const startDate = new Date(range.startDate)
      startDate.setHours(0, 0, 0, 0)
      const endDate = new Date(range.endDate)
      endDate.setHours(23, 59, 59, 999)

      const params = {
        registrationDateFrom: startDate.toISOString(),
        registrationDateTo: endDate.toISOString(),
      }

      // ATL-1477: every matching client, not the first 50.
      const rawClients = await readWholeList(
        (window) => clientsApi.getClientsWindow({ ...params, ...window }),
        { label: 'clients', order: newestFirst('registrationDate') },
      )
      const clients = rawClients.map(c => normalizeClient(c as ClientResponse))

      if (flat) {
        return clients
      } else {
        return groupClientsByDate(clients, 'registrationDate')
      }
    } catch (error) {
      console.error('Error fetching clients registered on date range from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'clients', function: 'getClientsRegisteredOnDateRange' },
        extra: { range, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation
  const startDate = new Date(range.startDate)
  startDate.setHours(0, 0, 0, 0)
  const endDate = new Date(range.endDate)
  endDate.setHours(23, 59, 59, 999)

  const querySnapshot = await (db as {
    collection: (name: string) => {
      where: (field: string, op: string, value: Date) => {
        where: (field: string, op: string, value: Date) => {
          orderBy: (field: string, direction: string) => {
            get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => Client }) => void) => void }>
          }
        }
      }
    }
  }).collection('clients')
    .where('registrationDate', '>=', startDate)
    .where('registrationDate', '<=', endDate)
    .orderBy('registrationDate', 'desc')
    .get()

  const clients: Client[] = []
  querySnapshot.forEach((doc: { id: string; ref: MockRef; data: () => Client }) => {
    const client = doc.data()
    client.id = doc.id
    client.ref = doc.ref
    clients.push(client)
  })

  if (flat) {
    return clients
  } else {
    return groupClientsByDate(clients, 'registrationDate')
  }
}

/**
 * Get clients active (had purchases) within a date range
 */
export const getClientsActiveOnDateRange = async (
  db: unknown,
  range: DateRange,
  flat = false
): Promise<Client[] | ClientsByDate[]> => {
  if (shouldUseSupabase()) {
    try {
      const startDate = new Date(range.startDate)
      startDate.setHours(0, 0, 0, 0)
      const endDate = new Date(range.endDate)
      endDate.setHours(23, 59, 59, 999)

      const params = {
        lastPurchaseDateFrom: startDate.toISOString(),
        lastPurchaseDateTo: endDate.toISOString(),
      }

      // ATL-1477: every matching client, not the first 50.
      const rawClients = await readWholeList(
        (window) => clientsApi.getClientsWindow({ ...params, ...window }),
        { label: 'clients', order: newestFirst('lastPurchaseDate') },
      )
      const clients = rawClients.map(c => normalizeClient(c as ClientResponse))

      if (flat) {
        return clients
      } else {
        return groupClientsByDate(clients, 'lastPurchaseDate')
      }
    } catch (error) {
      console.error('Error fetching active clients from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'clients', function: 'getClientsActiveOnDateRange' },
        extra: { range, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation
  const startDate = new Date(range.startDate)
  startDate.setHours(0, 0, 0, 0)
  const endDate = new Date(range.endDate)
  endDate.setHours(23, 59, 59, 999)

  const querySnapshot = await (db as {
    collection: (name: string) => {
      where: (field: string, op: string, value: Date) => {
        where: (field: string, op: string, value: Date) => {
          orderBy: (field: string, direction: string) => {
            get: () => Promise<{ forEach: (fn: (doc: { id: string; ref: MockRef; data: () => Client }) => void) => void }>
          }
        }
      }
    }
  }).collection('clients')
    .where('lastPurchaseDate', '>=', startDate)
    .where('lastPurchaseDate', '<=', endDate)
    .orderBy('lastPurchaseDate', 'desc')
    .get()

  const clients: Client[] = []
  querySnapshot.forEach((doc: { id: string; ref: MockRef; data: () => Client }) => {
    const client = doc.data()
    client.id = doc.id
    client.ref = doc.ref
    clients.push(client)
  })

  if (flat) {
    return clients
  } else {
    return groupClientsByDate(clients, 'lastPurchaseDate')
  }
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Add a new client
 */
export const addNewClient = async (
  db: unknown,
  info: CreateClientParams
): Promise<MockRef> => {
  assertWWriterAvailable('global/Firebase/clients.ts#addNewClient')
  // ATL-688: same cleared-field normalization as updateClient — a client created with an empty
  // bonus/discount/price-tier control must land as null in BOTH stores, not '' in Firestore.
  // ATL-1523: and the phone without the invisible characters a pasted number carries.
  const normalizedInfo = normalizeClientPhoneFields(normalizeClearedLoyaltyFields(info))

  const batch = rootDb.batch()
  const newClientRef = (db as { collection: (name: string) => { doc: () => MockRef } }).collection('clients').doc()

  batch.set(newClientRef, normalizedInfo)

  // Update daily stats
  const statFields = {
    newBonusClients: fieldValue.increment(1),
  }
  batch.set(
    (db as { collection: (name: string) => { doc: (id: string) => unknown } }).collection('dailyStats').doc(info.dailyStatID),
    {
      ...statFields,
      date: info.registrationDate,
      store: info.registrationSource,
      organizationId: info.organizationId,
    },
    { merge: true }
  )

  await commitGuardedCreate('clients.create', db, newClientRef, batch) // ATL-2004: a re-delivery cannot apply twice

  // Dual write to Supabase
  const birthdayValue = toApiDate(info.birthday)
  const registrationDateValue = toApiDate(info.registrationDate)
  // Pass both name and displayName - backend handles fallback
  await bestEffortDualWrite('clients.create', () => clientsApi.createClient({
    firestoreId: newClientRef.id,
    organizationId: info.organizationId,
    name: info.name,
    displayName: info.displayName,
    phone: normalizedInfo.phone,
    phoneNumber: normalizedInfo.phoneNumber,
    email: info.email,
    ...(birthdayValue ? { birthday: birthdayValue } : {}),
    bonusValue: info.bonusValue || 0,
    punchCardValue: info.punchCardValue || 0,
    ...(registrationDateValue ? { registrationDate: registrationDateValue } : {}),
    registrationSource: info.registrationSource,
    dailyStatId: info.dailyStatID,
    notes: info.notes,
    address: info.address,
    clientType: info.clientType,
    // ATL-688 — CreateClientView collects all three; they were dropped from this payload, so
    // the Supabase copy of every client lost its discount / bonus rate / price tier at creation.
    // Read off the normalized payload so a cleared control agrees with what Firestore got.
    loyaltyDiscount: normalizedInfo.loyaltyDiscount ?? null,
    bonusLevel: normalizedInfo.bonusLevel ?? null,
    preferredPriceType: normalizedInfo.preferredPriceType ?? null,
  } as Parameters<typeof clientsApi.createClient>[0]))

  return newClientRef
}

/**
 * Delete a client
 */
export const deleteClient = async (
  db: unknown,
  id: string
): Promise<void> => {
  assertWWriterAvailable('global/Firebase/clients.ts#deleteClient')
  await (db as { collection: (name: string) => { doc: (id: string) => { delete: () => Promise<void> } } })
    .collection('clients').doc(id).delete()

  // Dual write to Supabase
  await bestEffortDualWriteDelete('clients.delete', () => clientsApi.deleteClient(id), { firestoreId: id })
}

/**
 * Update a client
 */
export const updateClient = async (
  db: unknown,
  clientId: string,
  updates: UpdateClientParams
): Promise<boolean> => {
  assertWWriterAvailable('global/Firebase/clients.ts#updateClient')
  try {
    // ATL-688: the edit modal emits '' for a CLEARED bonus level / discount / price tier (the
    // bonus control is a free-text Input). Normalize to null BEFORE either write, so Firestore
    // and Supabase record the cleared field identically instead of ''-vs-NULL drift.
    const normalizedUpdates = normalizeClientPhoneFields(normalizeClearedLoyaltyFields(updates))

    // ATL-1978. Firestore is the write master, so its failure is this edit's failure: it throws
    // before the dual-write runs. It used to be caught and logged while the Supabase write went
    // ahead, and the edit then lived in Supabase only. That happened to every edit of a client whose
    // Firestore document has no `loyaltyDiscount` key: the edit form sends that field as
    // `undefined`, and Firestore (ignoreUndefinedProperties unset) rejects a payload carrying one.
    // `undefined` means "leave alone" (clientFieldNormalization), so it is dropped from the
    // Firestore payload; `null` stays and clears the field.
    const clientRef = (db as { collection: (name: string) => { doc: (id: string) => { update: (data: unknown) => Promise<void> } } })
      .collection('clients').doc(clientId)
    await clientRef.update(filterUndefined(normalizedUpdates as Record<string, unknown>))

    // Dual write to Supabase
    const birthdayValue = toApiDate(normalizedUpdates.birthday)
    await bestEffortDualWrite('clients.update', () => clientsApi.updateClient(clientId, {
      name: normalizedUpdates.name ?? normalizedUpdates.displayName,
      phoneNumber: normalizedUpdates.phoneNumber,
      email: normalizedUpdates.email,
      ...(normalizedUpdates.address !== undefined ? { address: normalizedUpdates.address } : {}),
      ...(birthdayValue ? { birthday: birthdayValue } : {}),
      ...(normalizedUpdates.bonus !== undefined ? { bonusBalance: normalizedUpdates.bonus, bonusValue: normalizedUpdates.bonus } : {}),
      ...(normalizedUpdates.punchCardPunches !== undefined ? { punchCardPunches: normalizedUpdates.punchCardPunches } : {}),
      notes: normalizedUpdates.notes,
      // ATL-688. Forwarded only when the caller actually supplied the key: `undefined` means
      // "leave alone" and `null` means "cleared" (the edit modal clears a discount that way),
      // so spreading conditionally is what preserves the distinction end-to-end.
      ...(normalizedUpdates.loyaltyDiscount !== undefined
        ? { loyaltyDiscount: normalizedUpdates.loyaltyDiscount }
        : {}),
      ...(normalizedUpdates.bonusLevel !== undefined
        ? { bonusLevel: normalizedUpdates.bonusLevel }
        : {}),
      ...(normalizedUpdates.preferredPriceType !== undefined
        ? { preferredPriceType: normalizedUpdates.preferredPriceType }
        : {}),
    } as Parameters<typeof clientsApi.updateClient>[1]))

    return true
  } catch (error) {
    console.error('Error updating client:', error)
    throw error
  }
}

// ============================================================================
// Bonus Operations
// ============================================================================

/**
 * Update bonus value for a client
 */
export const updateBonusForClient = async (
  db: unknown,
  clientId: string,
  currentBonusValue: number,
  value: number,
  isAddition: boolean
): Promise<void> => {
  // A zero change changes nothing. BonusValueChange starts at 0, so saving without typing lands
  // here; writing it would record an empty Firestore operation and send a zero delta the backend
  // refuses (400, dropped by the outbox as permanent). Checked before the floor: a no-op on a client
  // whose balance is already negative is still a no-op, not an error that aborts a bulk change.
  // Checked before the cut-over preview guard too (P9b review 1): a no-op writes nothing, so it is
  // not refused (a pure early return — `isPureEarlyReturn` in scripts/wWriterCensus.ts).
  if (value === 0) return
  assertWWriterAvailable('global/Firebase/clients.ts#updateBonusForClient')

  const newBonusValue = currentBonusValue + (isAddition ? value : -value)

  if (newBonusValue < 0) {
    throw new Error("Bonus value shouldn't be less than zero")
  }

  const batch = rootDb.batch()
  const delta = isAddition ? value : -value

  // Update client bonus
  batch.update(
    (db as { collection: (name: string) => { doc: (id: string) => unknown } }).collection('clients').doc(clientId),
    { bonusValue: fieldValue.increment(delta) }
  )

  // Create operation record. Its id is minted here so the Supabase mirror below can carry it: it is
  // the replay key the backend applies at most once (ATL-1445 stage 2c).
  const clientDocRef = (db as { collection: (name: string) => { doc: (id: string) => { collection: (name: string) => { doc: () => { id: string } } } } })
    .collection('clients').doc(clientId)
  const operationRef = clientDocRef.collection('operations').doc()
  const operationDate = new Date()
  batch.set(operationRef, {
    operationType: 'bonusChange',
    operationValue: delta,
    date: operationDate,
    clientId,
    userId: auth.currentUser?.uid,
    // Marks an operation whose Supabase mirror is the keyed DELTA below (stage 2c). Older bundles
    // write the same document but mirror an absolute balance, and nothing else tells the two apart.
    // A history backfill (ATL-1512) must NOT pre-insert this operation's key: the key is what makes
    // the delta's outbox replay answer "replayed", so pre-inserting it would drop an unapplied delta.
    // The marker says which mirror this bundle WOULD send, not that it arrived: an informed gate
    // deny (kill switch, org off the allowlist) never sends it; an uninformed skip (stale/absent
    // Remote Config) is replayed only IN SESSION, never durably, so an app restart before the
    // policy heals loses it; and a durably queued one can still expire (outbox MAX_ATTEMPTS /
    // MAX_AGE_MS). Only a re-baseline of the balance from Firestore (the migration step) may key
    // it after that.
    mirror: 'delta',
  })

  await batch.commit()

  // Dual write to Supabase — as a DELTA, keyed by the operation id (ATL-1445 stage 2c). It used to
  // send an ABSOLUTE `bonusBalance` computed from `currentBonusValue`, the balance this device last
  // read, which overwrote any accrual (a sale on another till) that landed in between; and it recorded
  // the operation through POST /client-operations, a route that never existed (ATL-1512). The backend
  // now adds the delta under a row lock and records the operation once, so a replay from the outbox
  // (which keys this entry by the operation id) cannot apply it twice.
  //
  // Declared `replaySafe`: the gate's in-session replay queue (dualWrite.ts) replays only
  // create-class ops after a stale/unavailable Remote Config skip, because an absolute update
  // replayed late would clobber a newer one. This write records one keyed client_operations row AND
  // adds the delta to bonus_balance; deltas commute and a replay of an applied key is a server
  // no-op, so a late replay is safe, while NOT replaying a skipped delta loses it for good (nothing
  // later heals a delta). That replay is in-session only (see the marker above).
  await bestEffortDualWrite(
    'clients.applyBonusAdjustment',
    () => clientsApi.applyBonusAdjustment(clientId, {
      operationId: operationRef.id,
      delta,
      date: operationDate.toISOString(),
    }),
    { firestoreId: operationRef.id, replaySafe: true },
  )
}

/**
 * Get current bonus value for a client
 */
export const getClientBonusValue = async (
  db: unknown,
  clientId: string
): Promise<number> => {
  if (shouldUseSupabase()) {
    try {
      const client = normalizeClient(await clientsApi.getClientById(clientId))
      return client.bonusValue ?? client.bonusBalance ?? 0
    } catch (error) {
      console.error('Error fetching client bonus value from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'clients', function: 'getClientBonusValue' },
        extra: { clientId, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation
  const client = await (db as { collection: (name: string) => { doc: (id: string) => { get: () => Promise<{ data: () => Client }> } } })
    .collection('clients').doc(clientId).get()
  return client.data().bonusValue || 0
}

/**
 * Get client operations history (manual bonus changes), newest first.
 *
 * Supabase read mode reads `GET /client-operations` (ATL-1512). That route did not exist until
 * 2026-09-27 — every call 404'd, and ATL-1472 read Firestore in both modes meanwhile. The table it
 * serves is now filled by the live writer (ATL-1445 stage 2c records each operation with its delta),
 * the old-bundle history ingest, the `clientOperations` migration step and the ATL-1512 backfill, and
 * the route answers in the Firestore document's own shape (`bonusChange`, a signed `operationValue`),
 * so both read modes render the same list. This read moved only after the backfill was applied and
 * verified — before that it would have silently read "no operations".
 */
/** `GET /client-operations` returns at most this many rows per request (PostgREST's cap). */
const CLIENT_OPERATIONS_PAGE = 1000

export const getClientOperations = async (
  db: unknown,
  clientId: string
): Promise<ClientOperation[]> => {
  if (shouldUseSupabase()) {
    try {
      // Paged to the end: the route returns at most CLIENT_OPERATIONS_PAGE rows per request.
      const operations: ClientOperation[] = []
      const seen = new Set<string>()
      for (let offset = 0; ; offset += CLIENT_OPERATIONS_PAGE) {
        const page = await clientOperationsApi.getClientOperations({
          clientId,
          limit: CLIENT_OPERATIONS_PAGE,
          offset,
        })
        for (const op of page) {
          // An operation recorded while paging shifts later pages by one, repeating a row; the log
          // is append-only (nothing is ever removed), so dropping repeats by id makes the read exact.
          if (seen.has(op.id)) continue
          seen.add(op.id)
          operations.push(transformApiItem(op as unknown as Record<string, unknown>, 'clientOperations') as unknown as ClientOperation)
        }
        if (page.length < CLIENT_OPERATIONS_PAGE) break
      }
      return operations
    } catch (error) {
      console.error('Error fetching client operations from Supabase:', error)
      Sentry.captureException(error, {
        tags: { supabase_fetch_error: true, collection: 'clients', function: 'getClientOperations' },
        extra: { clientId, errorMessage: (error as Error).message },
      })
      throw error
    }
  }

  // Firestore implementation
  const operations = await (db as {
    collection: (name: string) => {
      doc: (id: string) => {
        collection: (name: string) => {
          orderBy: (field: string, direction: string) => {
            get: () => Promise<{
              docs: Array<{ id: string; ref: MockRef; data: () => ClientOperation }>
            }>
          }
        }
      }
    }
  }).collection('clients').doc(clientId).collection('operations').orderBy('date', 'desc').get()

  return operations.docs.map(doc => ({
    ...doc.data(),
    id: doc.id,
    ref: doc.ref,
  }))
}
